import { Button, Flex, IconButton, Separator, Text } from "@radix-ui/themes";
import {
  ExploreCustomField,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import DateRangePicker, {
  DateRange,
} from "@/components/shared/date-range-picker-v2";
import { ExploreSearch } from "./ExploreSearch";
import {
  ExploreFilterMenu,
  ExploreFiltersValue,
} from "../explore-filter-menu/ExploreFilterMenu";
import { Pencil, Trash2 } from "lucide-react";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useCallback, useState } from "react";
import { ChevronDownIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { ExploreView } from "@/v2/entities/explore/Explore.model";
import { DeleteExploreViewDialog } from "@/routes/explore/delete-explore-view";
import { SubscribeToExploreViewDialog } from "./SubscribeToExploreViewDialog";
import { useExploreApi } from "@/v2/entities/explore/Explore.api";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Notifications03Icon } from "@/v2/assets/icons/Notifications03Icon";

import { ExploreGroupByColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreGroupByPickerTabs } from "./ExploreGroupByPicker";

interface ExploreViewBySlugHeaderProps {
  view: ExploreView;
  source: ExploreInteractionType;
  dateRange: DateRange;
  search: string;
  isEvaluated: boolean;
  userFilters: ExploreFiltersValue;
  filter: Filter;
  groupBy?: string;
  allowedGroupByOptions?: ExploreGroupByColumnType[];
  notAllowedGroupByOptions?: ExploreGroupByColumnType[];
  conversationCustomFields?: ExploreCustomField[];
  ticketCustomFields?: ExploreCustomField[];

  // Handlers
  onDateRangeChange: (value: DateRange) => void;
  onSearchChange: (search: string) => void;
  onUserFiltersChange: (value: ExploreFiltersValue) => void;
  onClearUserFilters: () => void;
  onGroupByChange: (groupBy?: string) => void;
}

export function ExploreViewBySlugHeader({
  view,
  source,
  dateRange,
  search,
  isEvaluated,
  userFilters,
  filter,
  groupBy,
  allowedGroupByOptions,
  notAllowedGroupByOptions,
  conversationCustomFields,
  ticketCustomFields,
  onDateRangeChange,
  onSearchChange,
  onUserFiltersChange,
  onClearUserFilters,
  onGroupByChange,
}: ExploreViewBySlugHeaderProps) {
  const navigate = useNavigate();
  const [deleteViewOpen, setDeleteViewOpen] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const { unsubscribeFromView } = useExploreApi();
  const queryClient = useQueryClient();

  const handleEditView = useCallback(
    (viewSlug: string) => {
      navigate(`/explore/views/${viewSlug}/edit`);
    },
    [navigate]
  );

  const handleReviewReport = useCallback(() => {
    navigate(`/custom-report/${view.id}`);
  }, [navigate, view.id]);

  const unsubscribeMutation = useMutation({
    mutationFn: async (viewId: string) => {
      return unsubscribeFromView(viewId);
    },
    onSuccess: () => {
      toast.success("Successfully unsubscribed from view reports");
      queryClient.invalidateQueries(["explore-view-by-slug", view.slug]);
    },
    onError: (error) => {
      toast.error("Failed to unsubscribe from view reports");
      console.error("Failed to unsubscribe:", error);
    },
  });

  const handleUnsubscribe = useCallback(() => {
    unsubscribeMutation.mutate(view.id);
  }, [unsubscribeMutation, view.id]);

  return (
    <Flex
      width={"calc(100% - 250px)"}
      direction={"column"}
      position={"fixed"}
      top={"0"}
      className="bg-background z-40"
    >
      {/* First Row: View Title + Actions */}
      <Flex justify={"between"} align={"center"} px={"3"} py={"3"}>
        <Text size="2" weight="bold">
          {view.title}
        </Text>

        {/* View Actions */}
        <Flex align="center" gap="4">
          {source === "tickets" && (
            <>
              {view.isSubscribed ? (
                <Flex
                  align="center"
                  style={{ borderRadius: "var(--radius-2)" }}
                >
                  <Button
                    size="2"
                    color="gray"
                    variant="solid"
                    style={{
                      backgroundColor: "#000",
                      color: "#fff",
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                    onClick={handleReviewReport}
                  >
                    Review Latest Report
                  </Button>
                  <DropdownMenu
                    items={[
                      {
                        label: "Unsubscribe",
                        onClick: handleUnsubscribe,
                      },
                    ]}
                  >
                    <DropdownMenu.Trigger>
                      <IconButton
                        size="2"
                        color="gray"
                        variant="solid"
                        style={{
                          backgroundColor: "#000",
                          color: "#fff",
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <ChevronDownIcon />
                      </IconButton>
                    </DropdownMenu.Trigger>
                  </DropdownMenu>
                </Flex>
              ) : (
                <Button
                  size="2"
                  color="gray"
                  variant="solid"
                  style={{ backgroundColor: "#000", color: "#fff" }}
                  onClick={() => setSubscribeDialogOpen(true)}
                >
                  <Notifications03Icon />
                  Subscribe
                </Button>
              )}
            </>
          )}
          {view && (
            <DropdownMenu
              items={[
                {
                  label: "Edit",
                  icon: <Pencil size={14} />,
                  onClick: () => handleEditView(view.slug),
                },
                { type: "separator" },
                {
                  label: "Delete",
                  icon: <Trash2 size={14} />,
                  onClick: () => setDeleteViewOpen(true),
                },
              ]}
            >
              <DropdownMenu.Trigger>
                <IconButton variant="ghost" size="2">
                  <DotsHorizontalIcon />
                </IconButton>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          )}
        </Flex>
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />

      <Flex
        justify={"between"}
        align={"center"}
        px={"3"}
        py={"6px"}
        overflow={"hidden"}
        gap={"2"}
      >
        <Flex flexGrow={"1"} flexShrink={"1"} overflow={"hidden"}>
          <ExploreGroupByPickerTabs
            source={source}
            groupBy={groupBy}
            setGroupBy={onGroupByChange}
            allowedGroupByOptions={allowedGroupByOptions}
            notAllowedGroupByOptions={notAllowedGroupByOptions}
            conversationCustomFields={conversationCustomFields}
            ticketCustomFields={ticketCustomFields}
          />
        </Flex>
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />

      {/* Second Row: Filters + Search + Date + View Mode */}
      <Flex px={"3"} py={"2"} justify={"between"} gap={"2"}>
        <ExploreFilterMenu
          size="2"
          source={source}
          value={userFilters}
          onChange={onUserFiltersChange}
          onClearAll={onClearUserFilters}
          label={
            isEvaluated
              ? `Evaluated ${exploreInteractionTypeToLabel[source].plural}`
              : `All ${exploreInteractionTypeToLabel[source].plural}`
          }
          filter={filter}
        />

        <Flex justify={"between"} gap={"2"} flexShrink={"0"} align={"center"}>
          <ExploreSearch search={search} onSearchChanged={onSearchChange} />

          <DateRangePicker
            size="2"
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </Flex>
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />

      {deleteViewOpen && (
        <DeleteExploreViewDialog
          open={deleteViewOpen}
          view={view}
          onClose={() => setDeleteViewOpen(false)}
        />
      )}

      <SubscribeToExploreViewDialog
        open={subscribeDialogOpen}
        onOpenChange={setSubscribeDialogOpen}
        view={view}
      />
    </Flex>
  );
}
